import { useContext, useMemo } from 'react';
import Link from 'next/link';
import { cn } from 'common/utils';
import { PinIcon } from 'components/v2/atoms/icon';
import { MediaTypeEnum, PostItemType } from 'service/timeline';

import ProfilePageContext from './profile-page-context';

// Pin post item component (similar to SmallPostBigThumb but using Tailwind)
const PinPostItem = ({ post }: { post: PostItemType }) => {
  const postDetailsUrl = `/${post.user_info.username}/posts/${post.id}`;

  // Get thumbnail URL based on media type (similar to SmallPostBigThumb logic)
  const thumbnailUrl = useMemo(() => {
    // Streaming thumbnail
    if (post?.streaming?.thumbnail) {
      return post.streaming.thumbnail;
    }
    // Media content
    if (post?.media && post.media[0]) {
      const media = post.media[0];
      if (media.type === MediaTypeEnum.Image) {
        return media.data.url;
      }
      if (
        media.type === MediaTypeEnum.Video ||
        media.type === MediaTypeEnum.Link
      ) {
        return media.data.thumbnail_url;
      }
      if (media.type === MediaTypeEnum.Audio) {
        return media.data.thumbnail_url || null;
      }
    }
    return null;
  }, [post]);

  // Check if post has video or streaming content
  const isVideoContent = useMemo(() => {
    if (post?.streaming?.thumbnail) return true;
    if (post?.media && post.media[0]) {
      return post.media[0].type === MediaTypeEnum.Video;
    }
    return false;
  }, [post]);

  // Check if post has audio content
  const isAudioContent = useMemo(() => {
    if (post?.media && post.media[0]) {
      return post.media[0].type === MediaTypeEnum.Audio;
    }
    return false;
  }, [post]);

  // Get post title or text
  const postTitle = useMemo(() => {
    if (post.title?.trim()) return post.title;
    return post.text?.replace(/\n/g, ' ')?.slice(0, 60) || '無題';
  }, [post]);

  // Render media content
  const renderMedia = () => {
    // Has thumbnail
    if (thumbnailUrl) {
      return (
        <>
          <img
            src={thumbnailUrl}
            alt={postTitle}
            className="size-full object-cover"
            onContextMenu={(e) => e.preventDefault()}
          />
          {/* Play button for video/streaming */}
          {isVideoContent && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex size-9 items-center justify-center rounded-full bg-pp-bg-primary">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="ml-0.5 text-pp-text-primary"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
          {/* Audio icon */}
          {isAudioContent && (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="white"
                className="drop-shadow-md"
              >
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
            </div>
          )}
        </>
      );
    }

    // Audio without thumbnail
    if (isAudioContent) {
      return (
        <div className="flex size-full items-center justify-center bg-pp-green-primary">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="white"
            className="drop-shadow-md"
          >
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
          </svg>
        </div>
      );
    }

    // No media - show avatar
    return (
      <div className="flex size-full items-center justify-center bg-pp-bg-tertiary">
        <img
          src={post.user_info?.avatar}
          alt={post.user_info?.name}
          className="size-12 rounded-full object-cover"
        />
      </div>
    );
  };

  return (
    <Link
      href={postDetailsUrl}
      className="group relative flex cursor-pointer gap-3 rounded-lg p-1.5 transition-colors hover:bg-pp-bg-secondary"
    >
      {/* Thumbnail */}
      <div
        className={cn(
          'relative size-[94px] flex-none overflow-hidden rounded-lg',
          {
            'bg-pp-green-primary': isAudioContent && !thumbnailUrl,
            'bg-pp-bg-tertiary': !isAudioContent || thumbnailUrl,
          },
        )}
      >
        {renderMedia()}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col justify-center overflow-hidden">
        <h4 className="line-clamp-2 text-sm font-bold leading-snug text-pp-text-primary group-hover:text-pp-green-primary">
          {postTitle}
        </h4>
        <div className="mt-1.5 flex items-center gap-1">
          <span className="line-clamp-3 text-xs text-pp-text-primary">
            {post.text}
          </span>
        </div>
      </div>
    </Link>
  );
};

const PinPostsSection = () => {
  const { pinPosts } = useContext(ProfilePageContext);

  if (!pinPosts || pinPosts.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg bg-pp-pinned-bg p-3">
      {/* Header with pin icon */}
      <div className="mb-3 flex items-center gap-2">
        <PinIcon className="size-5 text-pp-pinned-text" />
        <h3 className="text-sm font-semibold text-pp-pinned-text">
          固定された投稿
        </h3>
      </div>

      {/* Pin posts list */}
      <div className="flex flex-col gap-1.5">
        {pinPosts.map((post, index) => (
          <PinPostItem key={`pin-${post.id}-${index}`} post={post} />
        ))}
      </div>
    </div>
  );
};

export default PinPostsSection;
